import _ from 'lodash'
import { EntityState } from '@reduxjs/toolkit'
import { CommonState, Model } from '@/core/classes/Model'
import { Consultation } from './consultation.model'
import { CreateStaffDto, Staff } from './staff.model'

export type Receiver = {
  peerId: string
  staffId: string
}

export type Room = {
  _id: string
  streamer: string
  creator: Staff | string
  consultation?: Consultation | string
  currentConsultation?: string
  receivers: Receiver[]
  meetings: Record<string, string>
  status?: 'inactive' | 'available' | 'in_progress' | 'standby'
  groupId?: string
}

/** The room creator is the stethoscope streamer and the only trusted ECG source. */
export const getRoomCreatorId = (room: Pick<Room, 'creator'>): string => (typeof room.creator === 'string' ? room.creator : room.creator._id)

export type CreateRoomDto = {
  creator: string
  consultation?: string
  currentConsultation?: string | null
  streamer?: string
  status?: Room['status']
}

export type UpdateRoomDto = Partial<CreateRoomDto>

export type RoomState = CommonState & {
  rooms: EntityState<Room, Room['_id']>
}

class RoomModel extends Model {
  static instance: RoomModel

  static getInstance() {
    if (!RoomModel.instance) {
      RoomModel.instance = new RoomModel('room')
    }
    return RoomModel.instance
  }

  getInitialState(): RoomState {
    return _.merge({}, this.commonState, {
      rooms: {
        entities: {},
        ids: [],
      },
    })
  }

  addUserToRoom = async (consultationId: string): Promise<Receiver> => {
    return await this.httpClient.request<Receiver>('/rooms/user', 'POST', { consultation: consultationId })
  }

  createRoom = async (createRoomDto: CreateRoomDto): Promise<Room> => {
    return await this.httpClient.request<Room>('/rooms', 'POST', createRoomDto)
  }

  getRoom = async (roomOrConsultationId: string): Promise<Room> => {
    return await this.httpClient.request<Room>(`/rooms/${roomOrConsultationId}`, 'GET')
  }

  updateRoom = async (roomId: string, updateRoomDto: UpdateRoomDto): Promise<Room> => {
    return await this.httpClient.request<Room>(`/rooms/${roomId}`, 'PATCH', updateRoomDto)
  }

  sendEmergencyRoomInvitation = async (roomId: string, guest: CreateStaffDto) => {
    await this.httpClient.request(`/rooms/${roomId}/emergency-room-invitation`, 'POST', guest)
  }
}

export default RoomModel.getInstance()
